import { useEffect, useRef } from 'react';
import useStore from '../../../store';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 20;
const ZOOM_THRESHOLD_RATIO = 0.15; // 5% change in distance to activate zoom
const PAN_THRESHOLD_DISTANCE = 20; // 10px movement to activate pan

// Вспомогательная функция для расчета расстояния между двумя точками
function calculateDistance(point1, point2) {
  const dx = (point1.clientX || point1.x) - (point2.clientX || point2.x);
  const dy = (point1.clientY || point1.y) - (point2.clientY || point2.y);
  return Math.sqrt(dx * dx + dy * dy);
}

// Вспомогательная функция для расчета центральной точки между двумя точками
function calculateCenter(touch1, touch2) {
  return {
    x: (touch1.clientX + touch2.clientX) / 2,
    y: (touch1.clientY + touch2.clientY) / 2,
  };
}

function usePhoneZoom(boardCanvasRef) {
  const { ref } = useStore.getState();
  const { puzzle } = ref;
  const { state, zoom, methods, layout, boardCanvas: puzzleBoardCanvas } = puzzle;


  useEffect(() => {
    if (!state.isInitialized) return;

    const boardCanvas = boardCanvasRef.current;
    if (!boardCanvas) return;

    // --- Обработчики событий касания ---

    const handleTouchStart = (event) => {

      if (puzzle.state.draggingPiece) { return; }

      if (event.touches.length !== 2 || event.target !== boardCanvas) {
        if (zoom.isZooming) {
          zoom.isZooming = false;
          zoom.zoomActivated = false;
          zoom.panActivated = false;
        }
        return;
      }

      event.preventDefault();

      // --- Получаем свежие данные ---
      const currentBoardTransform = puzzle.boardTransform;

      zoom.isZooming = true;
      zoom.zoomActivated = false;
      zoom.panActivated = false;

      const [touch1, touch2] = event.touches;
      zoom.debug.touches = [touch1, touch2];

      // --- Запоминаем начальные значения ---
      zoom.initialDistance = calculateDistance(touch1, touch2);
      zoom.initialCenterClient = calculateCenter(touch1, touch2);
      zoom.initialBoardTransform = { ...currentBoardTransform };

      // --- Очищаем значения активации ---
      zoom.activationDistance = null;
      zoom.activationScale = null;
      zoom.activationCenter = null;
      zoom.activationTransform = null;
    };

    const handleTouchMove = (event) => {
      if (!zoom.isZooming || event.touches.length !== 2) {
        return;
      }

      event.preventDefault();

      // --- Получаем свежие данные ---
      const currentBoardContainerLayout = layout.boardContainer;
      const currentBoardCanvasLayout = puzzleBoardCanvas.layout;
      const currentBoardTransform = puzzle.boardTransform;

      if (!currentBoardContainerLayout || typeof currentBoardContainerLayout.left !== 'number' ||
        !currentBoardCanvasLayout || typeof currentBoardCanvasLayout.x !== 'number') {
        console.warn("Layout not ready for touch calculation.");
        return;
      }

      const [touch1, touch2] = event.touches;
      zoom.debug.touches = [touch1, touch2];

      // --- Текущие значения ---
      const currentDistance = calculateDistance(touch1, touch2);
      const currentCenter = calculateCenter(touch1, touch2);

      // --- Проверка порогов и активация ---
      const distanceDeltaRatio = zoom.initialDistance === 0 ? 0 : Math.abs(currentDistance - zoom.initialDistance) / zoom.initialDistance;
      const centerDelta = calculateDistance(currentCenter, zoom.initialCenterClient);

      zoom.debug.distanceDeltaRatio = distanceDeltaRatio;
      zoom.debug.centerDelta = centerDelta;

      const couldActivateZoom = distanceDeltaRatio > ZOOM_THRESHOLD_RATIO;
      const couldActivatePan = centerDelta > PAN_THRESHOLD_DISTANCE;

      const isFirstActivation = !zoom.zoomActivated && !zoom.panActivated;

      // Захватываем состояние *только* при первой активации (зума или пана)
      if (isFirstActivation && (couldActivateZoom || couldActivatePan)) {
        const currentTransformSnapshot = { ...currentBoardTransform };
        zoom.activationCenter = { ...currentCenter };
        zoom.activationTransform = { ...currentTransformSnapshot };
        zoom.activationDistance = currentDistance;
        zoom.activationScale = currentTransformSnapshot.scale;
      }

      // Устанавливаем флаги активации, если пороги пройдены
      if (couldActivateZoom) {
        zoom.zoomActivated = true;
      }
      if (couldActivatePan) {
        zoom.panActivated = true;
      }

      // --- Расчет новой трансформации (только если активно) ---
      if ((zoom.zoomActivated || zoom.panActivated) && zoom.activationTransform) {


        const refScale = zoom.activationScale;
        const refX = zoom.activationTransform.x;
        const refY = zoom.activationTransform.y;
        const refCenter = zoom.activationCenter;
        const refDistance = zoom.activationDistance;

        let targetScale;

        if (zoom.zoomActivated) {
          const scaleDelta = refDistance === 0 ? 1 : currentDistance / refDistance;
          targetScale = refScale * scaleDelta;
          targetScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, targetScale));
        } else {
          targetScale = refScale;
        }

        const refCenterRelContainerX = refCenter.x - currentBoardContainerLayout.left;
        const refCenterRelContainerY = refCenter.y - currentBoardContainerLayout.top;

        const refTotalX = refX + currentBoardCanvasLayout.x;
        const refTotalY = refY + currentBoardCanvasLayout.y;

        let scaleRatio = 1.0;
        if (refScale !== 0) {
          scaleRatio = targetScale / refScale;
        }

        let zoomAdjustedTotalX = refTotalX;
        let zoomAdjustedTotalY = refTotalY;

        if (Math.abs(scaleRatio - 1.0) > 0.001) {
          zoomAdjustedTotalX = refCenterRelContainerX * (1 - scaleRatio) + refTotalX * scaleRatio;
          zoomAdjustedTotalY = refCenterRelContainerY * (1 - scaleRatio) + refTotalY * scaleRatio;
        }

        let panDx = 0;
        let panDy = 0;
        if (zoom.panActivated) {
          panDx = currentCenter.x - refCenter.x;
          panDy = currentCenter.y - refCenter.y;
        }

        const newTotalX = zoomAdjustedTotalX + panDx;
        const newTotalY = zoomAdjustedTotalY + panDy;

        const targetX = newTotalX - currentBoardCanvasLayout.x;
        const targetY = newTotalY - currentBoardCanvasLayout.y;

        methods.setBoardTransform({ scale: targetScale, x: targetX, y: targetY });
      }
    };

    const handleTouchEnd = (event) => {
      if (zoom.isZooming && event.touches.length < 2) {
        zoom.isZooming = false;
        zoom.zoomActivated = false;
        zoom.panActivated = false;
        zoom.activationDistance = null;
        zoom.activationScale = null;
        zoom.activationCenter = null;
        zoom.activationTransform = null;
      }
    };

    const handleTouchCancel = (event) => {
      if (zoom.isZooming) {
        zoom.isZooming = false;
        zoom.zoomActivated = false;
        zoom.panActivated = false;
        zoom.activationDistance = null;
        zoom.activationScale = null;
        zoom.activationCenter = null;
        zoom.activationTransform = null;
      }
    };


    // --- Добавление слушателей ---
    boardCanvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    boardCanvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    boardCanvas.addEventListener('touchend', handleTouchEnd, { passive: false });
    boardCanvas.addEventListener('touchcancel', handleTouchCancel, { passive: false });


    // --- Очистка слушателей ---
    return () => {
      boardCanvas.removeEventListener('touchstart', handleTouchStart);
      boardCanvas.removeEventListener('touchmove', handleTouchMove);
      boardCanvas.removeEventListener('touchend', handleTouchEnd);
      boardCanvas.removeEventListener('touchcancel', handleTouchCancel);

      if (zoom.isZooming) {
        zoom.isZooming = false;
        zoom.zoomActivated = false;
        zoom.panActivated = false;
        zoom.activationDistance = null;
        zoom.activationScale = null;
        zoom.activationCenter = null;
        zoom.activationTransform = null;
      }
    };
  }, [state.isInitialized]);
}

export default usePhoneZoom;
